import { useMutation, UseMutationOptions } from '@tanstack/react-query';

import {
  ArchivePayCalendarRequest,
  payCalendarsService,
  useToast,
} from '@aslanpay/portal-common-lib';

import { getPayCalendarErrorDescription, PayCalendarBatchError } from 'app/utils/payCalendars';

const useArchivePayCalendars = ({
  onSuccess,
  onError,
}: UseMutationOptions<unknown, Error, ArchivePayCalendarRequest[]>) => {
  const toast = useToast();

  return useMutation<unknown, Error, ArchivePayCalendarRequest[]>(
    async (ids: ArchivePayCalendarRequest[]) => {
      const results = await Promise.allSettled(
        ids.map(({ id }) => payCalendarsService.archivePayCalendar(id))
      );
      const failures = results.filter(
        (result): result is PromiseRejectedResult => result.status === 'rejected'
      );

      if (failures.length === results.length) {
        throw new PayCalendarBatchError(failures.map((failure) => failure.reason));
      }

      if (failures.length > 0) {
        toast({
          position: 'top-right',
          status: 'warning',
          title: 'Some pay calendars were not archived',
          description: getPayCalendarErrorDescription(
            failures.map((failure) => failure.reason),
            'Sorry, some of the pay calendars were not archived. Please try again.'
          ),
        });
        return;
      }

      toast({
        position: 'top-right',
        status: 'success',
        title: `Pay calendar${ids.length > 1 ? 's' : ''} archived`,
        description: `${ids.length} ${
          ids.length > 1 ? 'pay calendars were' : 'pay calendar was'
        } successfully archived!`,
      });
    },
    {
      onSuccess: (data, variables, context) => {
        if (onSuccess) onSuccess(data, variables, context);
      },
      onError: (error, variables, context) => {
        const reasons = error instanceof PayCalendarBatchError ? error.reasons : [error];

        toast({
          position: 'top-right',
          status: 'error',
          title: 'Something went wrong',
          description: getPayCalendarErrorDescription(
            reasons,
            `Sorry, the ${
              variables.length > 1 ? 'pay calendars were' : 'pay calendar was'
            } not archived. Please try again.`
          ),
        });

        if (onError) onError(error, variables, context);
      },
    }
  );
};

export default useArchivePayCalendars;
